import React, { useMemo, useState } from 'react';

const FLAGS = [['held', 'Held'], ['apl', 'APL'], ['flagship', 'Flagship'], ['hedged', 'Hedged']];

function CheckList({ title, options, value, onChange, maxHeight = 150, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const shown = open || value.length > 0;
  return (
    <div className="qs-filter-block">
      <div className="qs-filter-title">
        <button type="button" className="qs-sac-toggle" onClick={() => setOpen((o) => !o)}>
          {shown ? '▾' : '▸'} {title}{value.length > 0 ? ` (${value.length})` : ''}
        </button>
        {value.length > 0 && <button type="button" className="qs-link" onClick={() => onChange([])}>clear</button>}
      </div>
      {shown && (
        <div className="qs-checklist" style={{ maxHeight }}>
          {options.map((o) => (
            <label key={o.value} className="qs-check">
              <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
              <span className="qs-check-label">{o.value}</span>
              <span className="qs-count">{o.count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Universe filters. Filters narrow the rows; the percentile cohort is always the whole
// Sub Asset Class, so the counts here are informational only.
export default function FilterPanel({ universe, filters, setFilters }) {
  const [sacOpen, setSacOpen] = useState({});
  const sacGroups = useMemo(() => {
    const groups = {};
    (universe?.sub_asset_classes || []).forEach((s) => {
      const g = s.asset_class_group || 'Other';
      (groups[g] = groups[g] || []).push(s);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [universe]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleSac = (v) => set('sub_asset_classes', filters.sub_asset_classes.includes(v)
    ? filters.sub_asset_classes.filter((x) => x !== v) : [...filters.sub_asset_classes, v]);
  const toggleGroup = (g, items) => {
    const all = items.every((s) => filters.sub_asset_classes.includes(s.value));
    const vals = items.map((s) => s.value);
    set('sub_asset_classes', all
      ? filters.sub_asset_classes.filter((x) => !vals.includes(x))
      : Array.from(new Set([...filters.sub_asset_classes, ...vals])));
  };

  if (!universe) return <div className="qs-muted">Loading universe…</div>;
  return (
    <div className="qs-filters">
      <div className="qs-filter-block">
        <div className="qs-filter-title">Search</div>
        <input id="qs-search" type="search" placeholder="name, APIR or TR code" value={filters.search}
          onChange={(e) => set('search', e.target.value)} />
      </div>
      <div className="qs-filter-block">
        <div className="qs-filter-title">
          Sub asset class{filters.sub_asset_classes.length > 0 ? ` (${filters.sub_asset_classes.length})` : ''}
          {filters.sub_asset_classes.length > 0 && (
            <button type="button" className="qs-link" onClick={() => set('sub_asset_classes', [])}>clear</button>
          )}
        </div>
        <div className="qs-sac-groups">
          {sacGroups.map(([g, items]) => {
            const open = sacOpen[g] ?? items.some((s) => filters.sub_asset_classes.includes(s.value));
            const nSel = items.filter((s) => filters.sub_asset_classes.includes(s.value)).length;
            return (
              <div key={g} className="qs-sac-group">
                <div className="qs-sac-head">
                  <button type="button" className="qs-sac-toggle" onClick={() => setSacOpen((o) => ({ ...o, [g]: !open }))}>
                    {open ? '▾' : '▸'} {g}{nSel ? ` (${nSel})` : ''}
                  </button>
                  <button type="button" className="qs-link" onClick={() => toggleGroup(g, items)}>
                    {nSel === items.length ? 'none' : 'all'}
                  </button>
                </div>
                {open && items.map((s) => (
                  <label key={s.value} className="qs-check qs-sac">
                    <input type="checkbox" checked={filters.sub_asset_classes.includes(s.value)} onChange={() => toggleSac(s.value)} />
                    <span className="qs-check-label">{s.value}</span>
                    <span className="qs-count">{s.count}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="qs-filter-block">
        <div className="qs-filter-title">Flags</div>
        <div className="qs-flags">
          {FLAGS.map(([k, label]) => (
            <label key={k} className="qs-flag-row">
              <span>{label} <span className="qs-count">{universe.flags?.[k]}</span></span>
              <select value={filters[k] === null ? '' : String(filters[k])}
                onChange={(e) => set(k, e.target.value === '' ? null : e.target.value === 'true')}>
                <option value="">any</option>
                <option value="true">yes</option>
                <option value="false">no</option>
              </select>
            </label>
          ))}
          <label className="qs-flag-row">
            <span>Min history (months)</span>
            <input type="number" min={0} step={12} value={filters.min_months}
              onChange={(e) => set('min_months', e.target.value)} style={{ width: 70 }} />
          </label>
        </div>
      </div>
      <CheckList title="Style" options={universe.styles} value={filters.styles} onChange={(v) => set('styles', v)} />
      <CheckList title="Tilt" options={universe.tilts} value={filters.tilts} onChange={(v) => set('tilts', v)} />
      <CheckList title="Platform" options={universe.platforms} value={filters.platforms} onChange={(v) => set('platforms', v)} maxHeight={110} />
    </div>
  );
}
